/**
 * FERRAMENTA CENTRAL DE BACKUP E RESTAURAÇÃO — APRESENTAÇÃO
 * PROJETO: INSPECT IA
 * ESCOPO: ADMINISTRAÇÃO > BACKUP E RESTAURAÇÃO
 */

import { router } from "../router/router.js";
import { contextStore } from "../auth/context-store.js";
import { platform } from "../../app/browser-platform.js";
import type { RequestContext } from "../../application/context/request-context.js";
import {
  type BackupManifest,
  type BackupPackage,
  type BackupValidationResult,
  type BackupStorageMetrics,
  type RestoreHistoryEntry,
  type BackupType,
  type BackupScope,
  generateRestoreConfirmationToken
} from "../../domain/backup/backup-models.js";

export default class BackupRestorePage extends HTMLElement {
  private metrics: BackupStorageMetrics | null = null;
  private backups: BackupManifest[] = [];
  private restoreHistory: RestoreHistoryEntry[] = [];
  private activeTab: "backups" | "import" | "history" = "backups";
  private isLoading = false;
  private alertMessage: { text: string; type: "success" | "error" | "info" } | null = null;

  // Create Modal
  private isCreateModalOpen = false;
  private createForm: { type: BackupType; scope: BackupScope; description: string } = {
    type: "MANUAL",
    scope: "FULL",
    description: ""
  };
  private isCreating = false;

  // Import / Upload
  private uploadedPackage: BackupPackage | null = null;
  private uploadedFileName = "";
  private validationResult: BackupValidationResult | null = null;
  private isValidating = false;

  // Restore Modal
  private isRestoreModalOpen = false;
  private restoreTarget: {
    backupId: string;
    type: string;
    isFromUpload: boolean;
    packageData?: BackupPackage;
  } | null = null;
  private confirmationInput = "";
  private createSafetySnapshot = true;
  private isRestoring = false;

  public connectedCallback(): void {
    this.loadAllData();
  }

  private getRequestContext(): RequestContext {
    const ctx = contextStore.get();
    return {
      clientId: ctx.activeClientId || "default-client",
      activeClientId: ctx.activeClientId || "default-client",
      userId: ctx.userId || "admin-user",
      professionalId: (ctx as any).professionalId || null,
      permissions: ctx.permissions || new Set(),
      correlationId: `REQ-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`
    } as any;
  }

  private async loadAllData(): Promise<void> {
    this.isLoading = true;
    this.render();
    try {
      const context = this.getRequestContext();
      const [backups, metrics, history] = await Promise.all([
        platform.backupService.listBackups(context),
        platform.backupService.getStorageMetrics(context),
        platform.restoreService.listRestoreHistory(context)
      ]);
      this.backups = [...backups];
      this.metrics = metrics;
      this.restoreHistory = [...history];
    } catch (err: any) {
      console.error("Falha ao carregar dados de backup:", err);
      this.alertMessage = {
        text: `Erro ao carregar dados: ${err?.message || String(err)}`,
        type: "error"
      };
    } finally {
      this.isLoading = false;
      this.render();
    }
  }

  private formatBytes(bytes: number): string {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }

  private formatDate(iso: string | null | undefined): string {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("pt-BR") + " " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
    } catch {
      return iso;
    }
  }

  private downloadJson(filename: string, content: string): void {
    const blob = new Blob([content], { type: "application/json;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  private async handleDownloadBackup(backupId: string): Promise<void> {
    try {
      const context = this.getRequestContext();
      const jsonStr = await platform.backupService.exportBackupJson(context, backupId);
      this.downloadJson(`${backupId}.json`, jsonStr);
      this.alertMessage = {
        text: `Backup ${backupId} exportado com sucesso.`,
        type: "success"
      };
      this.render();
    } catch (err: any) {
      this.alertMessage = {
        text: `Falha ao exportar backup: ${err?.message || String(err)}`,
        type: "error"
      };
      this.render();
    }
  }

  private async handleDeleteBackup(backupId: string): Promise<void> {
    if (!confirm(`Deseja realmente excluir permanentemente o backup ${backupId}? Esta ação não pode ser desfeita.`)) {
      return;
    }

    try {
      const context = this.getRequestContext();
      await platform.backupService.deleteBackup(context, backupId);
      this.alertMessage = {
        text: `Backup ${backupId} excluído com sucesso.`,
        type: "success"
      };
      await this.loadAllData();
    } catch (err: any) {
      this.alertMessage = {
        text: `Falha ao excluir backup: ${err?.message || String(err)}`,
        type: "error"
      };
      this.render();
    }
  }

  private async handleRetentionCleanup(): Promise<void> {
    if (!confirm("Executar rotina de limpeza de retenção agora? Backups manuais e snapshots antigos além das cotas serão removidos.")) {
      return;
    }

    this.isLoading = true;
    this.render();
    try {
      const context = this.getRequestContext();
      const result = await platform.backupService.runRetentionCleanup(context);
      this.alertMessage = {
        text: `Limpeza de retenção concluída: ${result.deletedBackupIds.length} backup(s) antigos removidos. Espaço liberado: ${this.formatBytes(result.totalFreedBytes)}.`,
        type: "success"
      };
      await this.loadAllData();
    } catch (err: any) {
      this.alertMessage = {
        text: `Erro ao executar limpeza de retenção: ${err?.message || String(err)}`,
        type: "error"
      };
      this.isLoading = false;
      this.render();
    }
  }

  private async handleCreateBackupSubmit(): Promise<void> {
    this.isCreating = true;
    this.render();
    try {
      const context = this.getRequestContext();
      const desc = this.createForm.description.trim();
      const pkg = await platform.backupService.createBackup(context, {
        type: this.createForm.type,
        scope: this.createForm.scope,
        ...(desc ? { description: desc } : {})
      });
      this.isCreateModalOpen = false;
      this.createForm = { type: "MANUAL", scope: "FULL", description: "" };
      this.alertMessage = {
        text: `Backup ${pkg.manifest.id} gerado com sucesso! (${this.formatBytes(pkg.manifest.sizeBytes)})`,
        type: "success"
      };
      await this.loadAllData();
    } catch (err: any) {
      this.alertMessage = {
        text: `Erro ao gerar backup: ${err?.message || String(err)}`,
        type: "error"
      };
      this.isCreating = false;
      this.render();
    }
  }

  private async handleFileUpload(file: File): Promise<void> {
    this.isValidating = true;
    this.uploadedFileName = file.name;
    this.validationResult = null;
    this.uploadedPackage = null;
    this.render();

    try {
      const text = await file.text();
      const context = this.getRequestContext();
      const result = await platform.restoreService.validateBackupPackage(context, text);
      this.validationResult = result;
      if (result.valid) {
        this.uploadedPackage = JSON.parse(text) as BackupPackage;
      }
    } catch (err: any) {
      this.validationResult = {
        valid: false,
        errors: [`Erro ao ler arquivo: ${err?.message || String(err)}`],
        warnings: [],
        compatible: false,
        entitySummary: {},
        integrityChecked: false
      };
    } finally {
      this.isValidating = false;
      this.render();
    }
  }

  private openRestoreModal(backupId: string, type: string, isFromUpload: boolean, packageData?: BackupPackage): void {
    this.restoreTarget = {
      backupId,
      type,
      isFromUpload,
      ...(packageData ? { packageData } : {})
    };
    this.confirmationInput = "";
    this.createSafetySnapshot = true;
    this.isRestoreModalOpen = true;
    this.render();
  }

  private async handleExecuteRestore(): Promise<void> {
    if (!this.restoreTarget) return;

    const expectedToken = generateRestoreConfirmationToken(this.restoreTarget.backupId);
    if (this.confirmationInput.trim().toUpperCase() !== expectedToken) {
      alert(`Token incorreto. Digite exatamente: ${expectedToken}`);
      return;
    }

    this.isRestoring = true;
    this.render();

    try {
      const context = this.getRequestContext();
      const result = await platform.restoreService.executeRestore(context, {
        backupId: this.restoreTarget.backupId,
        ...(this.restoreTarget.packageData ? { packageData: this.restoreTarget.packageData } : {}),
        confirmationToken: this.confirmationInput.trim().toUpperCase(),
        createSafetySnapshot: this.createSafetySnapshot
      });

      this.isRestoreModalOpen = false;
      this.restoreTarget = null;
      this.uploadedPackage = null;
      this.validationResult = null;
      this.uploadedFileName = "";

      const totalRestored = Object.values(result.restoredEntities).reduce((a, b) => a + b, 0);
      this.alertMessage = {
        text: `Restauração concluída com sucesso! ${totalRestored} registros processados em ${result.durationMs}ms. ${result.safetySnapshotId ? `Snapshot de segurança: ${result.safetySnapshotId}` : ""}`,
        type: "success"
      };

      await this.loadAllData();
    } catch (err: any) {
      this.alertMessage = {
        text: `Falha na restauração: ${err?.message || String(err)}`,
        type: "error"
      };
      this.isRestoring = false;
      this.render();
    }
  }

  private render(): void {
    const expectedToken = this.restoreTarget ? generateRestoreConfirmationToken(this.restoreTarget.backupId) : "";
    const isTokenMatch = this.restoreTarget ? this.confirmationInput.trim().toUpperCase() === expectedToken : false;

    this.innerHTML = `
      <div style="display: flex; flex-direction: column; min-height: 100%; gap: 20px; background: var(--color-bg-primary); padding: 24px 32px; box-sizing: border-box;">

        <!-- Top Breadcrumb & Actions Header -->
        <header style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid var(--color-graphite-200); padding-bottom: 16px; flex-wrap: wrap; gap: 12px;">
          <div>
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">
              <button id="btn-back-admin" style="background: transparent; border: none; color: var(--color-text-secondary); cursor: pointer; display: flex; align-items: center; gap: 4px; font-size: 0.8125rem; padding: 0;">
                <span>← Administração</span>
              </button>
              <span style="color: var(--color-graphite-300);">/</span>
              <span style="font-size: 0.8125rem; color: var(--ia-primary); font-weight: 600;">Backup & Restauração</span>
            </div>
            <h1 style="color: var(--color-text-primary); font-size: 1.625rem; font-weight: 700; margin: 0; display: flex; align-items: center; gap: 10px;">
              <span>💾</span>
              <span>Ferramenta Central de Backup e Restauração</span>
            </h1>
            <p style="color: var(--color-text-secondary); font-size: 0.875rem; margin-top: 4px; margin-bottom: 0;">
              Exportação integral, recuperação transacional com SHA-256 e snapshots de segurança automatizados.
            </p>
          </div>

          <div style="display: flex; gap: 10px; align-items: center;">
            <button id="btn-retention-cleanup" style="background: var(--color-surface-secondary); color: var(--color-text-primary); border: 1px solid var(--color-graphite-300); padding: 8px 14px; border-radius: 6px; font-weight: 600; cursor: pointer; font-size: 0.8125rem; display: flex; align-items: center; gap: 6px; transition: all 0.2s;">
              <span>🧹</span>
              <span>Limpeza de Retenção</span>
            </button>
            <button id="btn-open-create-backup" style="background: var(--ia-action-bg, var(--color-primary)); color: var(--ia-action-fg, #F7F8F8); border: none; padding: 8px 18px; border-radius: 6px; font-weight: 600; cursor: pointer; font-size: 0.875rem; display: flex; align-items: center; gap: 6px; box-shadow: 0 1px 3px rgba(0,0,0,0.1);">
              <span>+</span>
              <span>Criar Backup Agora</span>
            </button>
          </div>
        </header>

        <!-- Alert Notification Banner -->
        ${this.alertMessage ? `
          <div style="display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; border-radius: 8px; font-size: 0.875rem; font-weight: 500; ${
            this.alertMessage.type === "success"
              ? "background: rgba(16, 185, 129, 0.12); border: 1px solid #10B981; color: #047857;"
              : this.alertMessage.type === "error"
              ? "background: rgba(239, 68, 68, 0.12); border: 1px solid #EF4444; color: #B91C1C;"
              : "background: rgba(59, 130, 246, 0.12); border: 1px solid #3B82F6; color: #1D4ED8;"
          }">
            <div style="display: flex; align-items: center; gap: 8px;">
              <span>${this.alertMessage.type === "success" ? "✅" : this.alertMessage.type === "error" ? "⚠️" : "ℹ️"}</span>
              <span>${this.alertMessage.text}</span>
            </div>
            <button id="btn-close-alert" style="background: transparent; border: none; cursor: pointer; color: inherit; font-size: 1rem; padding: 0 4px;">✕</button>
          </div>
        ` : ""}

        <!-- Storage & Status Metric KPI Cards -->
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 16px;">
          <!-- Metric 1: Total Backups -->
          <div style="background: var(--color-surface-primary); border: 1px solid var(--color-graphite-200); border-radius: 10px; padding: 16px 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.04);">
            <div style="display: flex; justify-content: space-between; align-items: flex-start;">
              <span style="font-size: 0.8125rem; font-weight: 600; color: var(--color-text-secondary); text-transform: uppercase; letter-spacing: 0.5px;">Snapshots & Backups</span>
              <span style="font-size: 1.25rem;">📦</span>
            </div>
            <div style="font-size: 1.75rem; font-weight: 700; color: var(--color-text-primary); margin-top: 8px;">
              ${this.metrics?.totalBackups ?? this.backups.length}
            </div>
            <div style="display: flex; gap: 8px; font-size: 0.75rem; color: var(--color-text-secondary); margin-top: 4px;">
              <span>${this.metrics?.manualCount ?? 0} manuais</span>
              <span>•</span>
              <span>${this.metrics?.safetySnapshotCount ?? 0} segurança</span>
            </div>
          </div>

          <!-- Metric 2: Storage Size -->
          <div style="background: var(--color-surface-primary); border: 1px solid var(--color-graphite-200); border-radius: 10px; padding: 16px 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.04);">
            <div style="display: flex; justify-content: space-between; align-items: flex-start;">
              <span style="font-size: 0.8125rem; font-weight: 600; color: var(--color-text-secondary); text-transform: uppercase; letter-spacing: 0.5px;">Volume de Armazenamento</span>
              <span style="font-size: 1.25rem;">🗄️</span>
            </div>
            <div style="font-size: 1.75rem; font-weight: 700; color: var(--color-text-primary); margin-top: 8px;">
              ${this.formatBytes(this.metrics?.totalSizeBytes ?? 0)}
            </div>
            <div style="font-size: 0.75rem; color: var(--color-text-secondary); margin-top: 4px;">
              Tamanho consolidado dos snapshots
            </div>
          </div>

          <!-- Metric 3: Last Backup -->
          <div style="background: var(--color-surface-primary); border: 1px solid var(--color-graphite-200); border-radius: 10px; padding: 16px 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.04);">
            <div style="display: flex; justify-content: space-between; align-items: flex-start;">
              <span style="font-size: 0.8125rem; font-weight: 600; color: var(--color-text-secondary); text-transform: uppercase; letter-spacing: 0.5px;">Última Geração</span>
              <span style="font-size: 1.25rem;">⏱️</span>
            </div>
            <div style="font-size: 1.125rem; font-weight: 700; color: var(--color-text-primary); margin-top: 12px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
              ${this.formatDate(this.metrics?.lastBackupDate ?? (this.backups[0]?.createdAt || null))}
            </div>
            <div style="font-size: 0.75rem; color: var(--color-text-secondary); margin-top: 4px;">
              ${this.backups.length > 0 ? `ID: ${this.backups[0]?.id}` : "Nenhum backup criado"}
            </div>
          </div>

          <!-- Metric 4: Health Status -->
          <div style="background: var(--color-surface-primary); border: 1px solid var(--color-graphite-200); border-radius: 10px; padding: 16px 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.04);">
            <div style="display: flex; justify-content: space-between; align-items: flex-start;">
              <span style="font-size: 0.8125rem; font-weight: 600; color: var(--color-text-secondary); text-transform: uppercase; letter-spacing: 0.5px;">Status de Integridade</span>
              <span style="font-size: 1.25rem;">🛡️</span>
            </div>
            <div style="display: flex; align-items: center; gap: 8px; margin-top: 10px;">
              <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background: #10B981;"></span>
              <span style="font-size: 1.125rem; font-weight: 700; color: #047857;">SAUDÁVEL</span>
            </div>
            <div style="font-size: 0.75rem; color: var(--color-text-secondary); margin-top: 4px;">
              Criptografia RFC-6234 SHA-256 ativa
            </div>
          </div>
        </div>

        <!-- Navigation Tabs -->
        <div style="display: flex; gap: 4px; border-bottom: 2px solid var(--color-graphite-200); padding-bottom: 0;">
          <button class="nav-tab ${this.activeTab === "backups" ? "active" : ""}" data-tab="backups" style="padding: 10px 18px; border: none; background: transparent; font-size: 0.875rem; font-weight: 600; cursor: pointer; border-bottom: 3px solid ${this.activeTab === "backups" ? "var(--ia-primary)" : "transparent"}; color: ${this.activeTab === "backups" ? "var(--ia-primary)" : "var(--color-text-secondary)"}; margin-bottom: -2px; display: flex; align-items: center; gap: 6px;">
            <span>📦 Backups e Snapshots</span>
            <span style="background: var(--color-surface-secondary); padding: 2px 6px; border-radius: 10px; font-size: 0.75rem;">${this.backups.length}</span>
          </button>
          <button class="nav-tab ${this.activeTab === "import" ? "active" : ""}" data-tab="import" style="padding: 10px 18px; border: none; background: transparent; font-size: 0.875rem; font-weight: 600; cursor: pointer; border-bottom: 3px solid ${this.activeTab === "import" ? "var(--ia-primary)" : "transparent"}; color: ${this.activeTab === "import" ? "var(--ia-primary)" : "var(--color-text-secondary)"}; margin-bottom: -2px; display: flex; align-items: center; gap: 6px;">
            <span>📥 Importar e Restaurar JSON</span>
          </button>
          <button class="nav-tab ${this.activeTab === "history" ? "active" : ""}" data-tab="history" style="padding: 10px 18px; border: none; background: transparent; font-size: 0.875rem; font-weight: 600; cursor: pointer; border-bottom: 3px solid ${this.activeTab === "history" ? "var(--ia-primary)" : "transparent"}; color: ${this.activeTab === "history" ? "var(--ia-primary)" : "var(--color-text-secondary)"}; margin-bottom: -2px; display: flex; align-items: center; gap: 6px;">
            <span>📜 Histórico de Restaurações</span>
            <span style="background: var(--color-surface-secondary); padding: 2px 6px; border-radius: 10px; font-size: 0.75rem;">${this.restoreHistory.length}</span>
          </button>
        </div>

        <!-- Tab Content Area -->
        <div style="flex: 1; display: flex; flex-direction: column;">

          <!-- TAB 1: BACKUPS LIST -->
          ${this.activeTab === "backups" ? `
            <div style="background: var(--color-surface-primary); border-radius: 10px; border: 1px solid var(--color-graphite-200); overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.03);">
              <div style="overflow-x: auto;">
                <table style="width: 100%; border-collapse: collapse; text-align: left; font-size: 0.875rem;">
                  <thead style="background: var(--color-surface-secondary); border-bottom: 1px solid var(--color-graphite-200);">
                    <tr>
                      <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">ID DO BACKUP</th>
                      <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">TIPO</th>
                      <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">DATA & HORA</th>
                      <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">OPERADOR</th>
                      <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">TAMANHO</th>
                      <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">ENTIDADES</th>
                      <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600; text-align: right;">AÇÕES</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${this.backups.length === 0 ? `
                      <tr>
                        <td colspan="7" style="padding: 48px 16px; text-align: center; color: var(--color-text-secondary);">
                          <div style="font-size: 2rem; margin-bottom: 8px;">📭</div>
                          <div style="font-weight: 600; color: var(--color-text-primary);">Nenhum backup encontrado</div>
                          <p style="font-size: 0.8125rem; margin-top: 4px; margin-bottom: 16px;">Gere o primeiro snapshot clicando no botão "Criar Backup Agora".</p>
                        </td>
                      </tr>
                    ` : this.backups.map(b => {
                      const totalEnt = Object.values(b.entityCounts || {}).reduce((x, y) => x + y, 0);
                      const isSafety = b.type === "SAFETY_SNAPSHOT";
                      return `
                        <tr style="border-bottom: 1px solid var(--color-graphite-100); transition: background 0.15s;">
                          <td style="padding: 12px 16px; font-family: monospace; font-weight: 600; color: var(--color-ink);">
                            ${b.id}
                            ${b.description ? `<div style="font-size: 0.75rem; color: var(--color-text-secondary); font-family: sans-serif; font-weight: 400; margin-top: 2px;">${b.description}</div>` : ""}
                          </td>
                          <td style="padding: 12px 16px;">
                            <span style="background: ${isSafety ? "rgba(245, 158, 11, 0.12)" : "rgba(16, 185, 129, 0.12)"}; color: ${isSafety ? "#D97706" : "#059669"}; border: 1px solid ${isSafety ? "rgba(245, 158, 11, 0.3)" : "rgba(16, 185, 129, 0.3)"}; padding: 3px 8px; border-radius: 6px; font-size: 0.75rem; font-weight: 600;">
                              ${isSafety ? "🛡️ SAFETY SNAPSHOT" : b.type}
                            </span>
                          </td>
                          <td style="padding: 12px 16px; color: var(--color-text-primary); white-space: nowrap;">
                            ${this.formatDate(b.createdAt)}
                          </td>
                          <td style="padding: 12px 16px; color: var(--color-text-secondary);">
                            ${b.createdBy}
                          </td>
                          <td style="padding: 12px 16px; font-family: monospace; color: var(--color-text-primary);">
                            ${this.formatBytes(b.sizeBytes)}
                          </td>
                          <td style="padding: 12px 16px; color: var(--color-text-secondary);">
                            <span title="${Object.entries(b.entityCounts || {}).map(([k, v]) => `${k}: ${v}`).join(", ")}" style="cursor: help; text-decoration: underline dotted;">
                              ${totalEnt} itens (${Object.keys(b.entityCounts || {}).length} tabelas)
                            </span>
                          </td>
                          <td style="padding: 12px 16px; text-align: right; white-space: nowrap;">
                            <button class="btn-download-backup" data-id="${b.id}" title="Baixar Pacote JSON" style="background: var(--color-surface-secondary); border: 1px solid var(--color-graphite-300); border-radius: 4px; padding: 5px 9px; font-size: 0.75rem; cursor: pointer; color: var(--color-text-primary); margin-right: 4px;">
                              📥 Baixar
                            </button>
                            <button class="btn-trigger-restore" data-id="${b.id}" data-type="${b.type}" title="Restaurar este Backup" style="background: rgba(16, 185, 129, 0.1); border: 1px solid #10B981; border-radius: 4px; padding: 5px 9px; font-size: 0.75rem; cursor: pointer; color: #047857; font-weight: 600; margin-right: 4px;">
                              🔄 Restaurar
                            </button>
                            <button class="btn-delete-backup" data-id="${b.id}" title="Excluir Backup" style="background: rgba(239, 68, 68, 0.1); border: 1px solid #EF4444; border-radius: 4px; padding: 5px 9px; font-size: 0.75rem; cursor: pointer; color: #DC2626;">
                              🗑️
                            </button>
                          </td>
                        </tr>
                      `;
                    }).join("")}
                  </tbody>
                </table>
              </div>
            </div>
          ` : ""}

          <!-- TAB 2: IMPORT & VALIDATE JSON -->
          ${this.activeTab === "import" ? `
            <div style="display: flex; flex-direction: column; gap: 20px;">
              <!-- Upload Dropzone Area -->
              <div id="dropzone" style="border: 2px dashed var(--color-graphite-300); border-radius: 12px; padding: 36px; background: var(--color-surface-primary); text-align: center; cursor: pointer; transition: all 0.2s;">
                <input type="file" id="input-backup-file" accept=".json" style="display: none;">
                <div style="font-size: 2.5rem; margin-bottom: 12px;">📁</div>
                <h3 style="margin: 0; font-size: 1.125rem; font-weight: 600; color: var(--color-text-primary);">
                  Clique ou arraste um arquivo de backup (.json) para restaurar
                </h3>
                <p style="color: var(--color-text-secondary); font-size: 0.875rem; margin-top: 6px; margin-bottom: 16px;">
                  O pacote será validado automaticamente por SHA-256 e verificação de integridade de esquema antes da confirmação.
                </p>
                <button type="button" id="btn-select-file" style="background: var(--ia-action-bg, var(--color-primary)); color: var(--ia-action-fg, #F7F8F8); border: none; padding: 8px 20px; border-radius: 6px; font-size: 0.875rem; font-weight: 600; cursor: pointer;">
                  Selecionar Arquivo .JSON
                </button>
              </div>

              ${this.isValidating ? `
                <div style="padding: 24px; background: var(--color-surface-primary); border-radius: 10px; border: 1px solid var(--color-graphite-200); text-align: center;">
                  <div style="font-size: 1.5rem; margin-bottom: 8px;">⏳</div>
                  <div style="font-weight: 600; color: var(--color-text-primary);">Verificando integridade criptográfica do pacote...</div>
                  <div style="font-size: 0.8125rem; color: var(--color-text-secondary); margin-top: 4px;">Calculando checksum SHA-256 RFC-6234 e validando esquema.</div>
                </div>
              ` : ""}

              <!-- Validation Results Card -->
              ${this.validationResult ? `
                <div style="background: var(--color-surface-primary); border-radius: 10px; border: 1px solid ${this.validationResult.valid ? "#10B981" : "#EF4444"}; padding: 24px; box-shadow: 0 1px 4px rgba(0,0,0,0.05);">
                  <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; border-bottom: 1px solid var(--color-graphite-200); padding-bottom: 12px;">
                    <div>
                      <div style="display: flex; align-items: center; gap: 8px;">
                        <span style="font-size: 1.25rem;">${this.validationResult.valid ? "✅" : "❌"}</span>
                        <h3 style="margin: 0; font-size: 1.125rem; font-weight: 700; color: ${this.validationResult.valid ? "#047857" : "#B91C1C"};">
                          ${this.validationResult.valid ? "Pacote Válido e Aprovado para Restauração" : "Pacote Inválido ou Incompatível"}
                        </h3>
                      </div>
                      <span style="font-size: 0.8125rem; color: var(--color-text-secondary); font-family: monospace; margin-top: 4px; display: inline-block;">
                        Arquivo: ${this.uploadedFileName}
                      </span>
                    </div>

                    ${this.validationResult.valid ? `
                      <button id="btn-proceed-restore-upload" style="background: #10B981; color: #FFFFFF; border: none; padding: 10px 20px; border-radius: 6px; font-weight: 700; font-size: 0.875rem; cursor: pointer; display: flex; align-items: center; gap: 6px; box-shadow: 0 1px 3px rgba(0,0,0,0.1);">
                        <span>🔄</span>
                        <span>Confirmar e Restaurar Pacote</span>
                      </button>
                    ` : ""}
                  </div>

                  <!-- Details Grid -->
                  <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 16px; margin-bottom: 16px;">
                    <div style="background: var(--color-surface-secondary); padding: 12px 14px; border-radius: 8px;">
                      <span style="font-size: 0.75rem; font-weight: 600; color: var(--color-text-secondary);">ID DO MANIFESTO</span>
                      <div style="font-family: monospace; font-weight: 600; font-size: 0.875rem; color: var(--color-text-primary); margin-top: 4px;">
                        ${this.validationResult.manifest?.id ?? "—"}
                      </div>
                    </div>
                    <div style="background: var(--color-surface-secondary); padding: 12px 14px; border-radius: 8px;">
                      <span style="font-size: 0.75rem; font-weight: 600; color: var(--color-text-secondary);">VERSÃO DO ESQUEMA</span>
                      <div style="font-family: monospace; font-weight: 600; font-size: 0.875rem; color: var(--color-text-primary); margin-top: 4px;">
                        v${this.validationResult.manifest?.schemaVersion ?? "—"} (Compatível: ${this.validationResult.compatible ? "Sim" : "Não"})
                      </div>
                    </div>
                    <div style="background: var(--color-surface-secondary); padding: 12px 14px; border-radius: 8px;">
                      <span style="font-size: 0.75rem; font-weight: 600; color: var(--color-text-secondary);">CHECKSUM SHA-256</span>
                      <div style="font-family: monospace; font-weight: 500; font-size: 0.75rem; color: var(--color-text-primary); margin-top: 4px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" title="${this.validationResult.manifest?.sha256Checksum ?? ""}">
                        ${this.validationResult.manifest?.sha256Checksum?.substring(0, 24) ?? "—"}...
                      </div>
                    </div>
                  </div>

                  ${this.validationResult.errors.length > 0 ? `
                    <div style="background: rgba(239, 68, 68, 0.08); border: 1px solid #EF4444; border-radius: 8px; padding: 12px 16px; margin-bottom: 16px;">
                      <div style="font-weight: 600; color: #B91C1C; font-size: 0.875rem; margin-bottom: 4px;">Erros encontrados:</div>
                      <ul style="margin: 0; padding-left: 20px; font-size: 0.8125rem; color: #991B1B;">
                        ${this.validationResult.errors.map(e => `<li>${e}</li>`).join("")}
                      </ul>
                    </div>
                  ` : ""}

                  <!-- Entities Summary -->
                  <div>
                    <h4 style="margin: 0 0 8px 0; font-size: 0.875rem; font-weight: 600; color: var(--color-text-primary);">
                      Entidades Detectadas no Pacote:
                    </h4>
                    <div style="display: flex; flex-wrap: wrap; gap: 8px;">
                      ${Object.entries(this.validationResult.entitySummary).map(([k, v]) => `
                        <div style="background: var(--color-surface-secondary); border: 1px solid var(--color-graphite-200); border-radius: 6px; padding: 6px 10px; font-size: 0.75rem; display: flex; gap: 6px; align-items: center;">
                          <span style="color: var(--color-text-secondary);">${k}:</span>
                          <span style="font-weight: 700; color: var(--color-text-primary);">${v}</span>
                        </div>
                      `).join("")}
                    </div>
                  </div>
                </div>
              ` : ""}
            </div>
          ` : ""}

          <!-- TAB 3: RESTORE HISTORY -->
          ${this.activeTab === "history" ? `
            <div style="background: var(--color-surface-primary); border-radius: 10px; border: 1px solid var(--color-graphite-200); overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.03);">
              <div style="overflow-x: auto;">
                <table style="width: 100%; border-collapse: collapse; text-align: left; font-size: 0.875rem;">
                  <thead style="background: var(--color-surface-secondary); border-bottom: 1px solid var(--color-graphite-200);">
                    <tr>
                      <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">ID DO LOG</th>
                      <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">DATA & HORA</th>
                      <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">OPERADOR</th>
                      <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">BACKUP ORIGEM</th>
                      <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">SNAPSHOT DE SEGURANÇA</th>
                      <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">STATUS</th>
                      <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">DETALHES</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${this.restoreHistory.length === 0 ? `
                      <tr>
                        <td colspan="7" style="padding: 48px 16px; text-align: center; color: var(--color-text-secondary);">
                          <div style="font-size: 2rem; margin-bottom: 8px;">📖</div>
                          <div style="font-weight: 600; color: var(--color-text-primary);">Nenhum registro de restauração</div>
                          <p style="font-size: 0.8125rem; margin-top: 4px;">O histórico registrará todas as operações de recuperação executadas.</p>
                        </td>
                      </tr>
                    ` : this.restoreHistory.map(h => {
                      const isSuccess = h.status === "SUCCESS";
                      const totalRest = Object.values(h.entityCounts || {}).reduce((x, y) => x + y, 0);
                      return `
                        <tr style="border-bottom: 1px solid var(--color-graphite-100);">
                          <td style="padding: 12px 16px; font-family: monospace; font-size: 0.8125rem; color: var(--color-text-primary);">
                            ${h.id}
                          </td>
                          <td style="padding: 12px 16px; color: var(--color-text-primary); white-space: nowrap;">
                            ${this.formatDate(h.restoredAt)}
                          </td>
                          <td style="padding: 12px 16px; color: var(--color-text-secondary);">
                            ${h.restoredBy}
                          </td>
                          <td style="padding: 12px 16px; font-family: monospace; font-weight: 600; color: var(--color-ink);">
                            ${h.sourceBackupId}
                          </td>
                          <td style="padding: 12px 16px; font-family: monospace; font-size: 0.8125rem; color: ${h.safetySnapshotId !== "NENHUM" ? "#D97706" : "var(--color-text-secondary)"};">
                            ${h.safetySnapshotId}
                          </td>
                          <td style="padding: 12px 16px;">
                            <span style="background: ${isSuccess ? "rgba(16, 185, 129, 0.12)" : "rgba(239, 68, 68, 0.12)"}; color: ${isSuccess ? "#059669" : "#DC2626"}; border: 1px solid ${isSuccess ? "rgba(16, 185, 129, 0.3)" : "rgba(239, 68, 68, 0.3)"}; padding: 2px 8px; border-radius: 6px; font-size: 0.75rem; font-weight: 600;">
                              ${isSuccess ? "SUCESSO" : "FALHA"}
                            </span>
                          </td>
                          <td style="padding: 12px 16px; font-size: 0.8125rem; color: var(--color-text-secondary);" title="${h.details}">
                            ${totalRest > 0 ? `${totalRest} registros restaurados` : h.details}
                          </td>
                        </tr>
                      `;
                    }).join("")}
                  </tbody>
                </table>
              </div>
            </div>
          ` : ""}

        </div>

        <!-- CREATE BACKUP MODAL -->
        ${this.isCreateModalOpen ? `
          <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 20px;">
            <div style="background: var(--color-surface-primary); border-radius: 12px; width: 100%; max-width: 520px; box-shadow: 0 10px 25px rgba(0,0,0,0.2); overflow: hidden; border: 1px solid var(--color-graphite-200);">
              <div style="padding: 20px 24px; border-bottom: 1px solid var(--color-graphite-200); display: flex; justify-content: space-between; align-items: center;">
                <h3 style="margin: 0; font-size: 1.125rem; font-weight: 700; color: var(--color-text-primary); display: flex; align-items: center; gap: 8px;">
                  <span>💾</span>
                  <span>Gerar Novo Backup</span>
                </h3>
                <button id="btn-close-create-modal" style="background: transparent; border: none; font-size: 1.25rem; cursor: pointer; color: var(--color-text-secondary);">✕</button>
              </div>

              <div style="padding: 24px; display: flex; flex-direction: column; gap: 16px;">
                <div>
                  <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-primary); margin-bottom: 6px;">Tipo de Snapshot</label>
                  <select id="create-backup-type" style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box;">
                    <option value="MANUAL" ${this.createForm.type === "MANUAL" ? "selected" : ""}>MANUAL — Backup Operacional do Usuário</option>
                    <option value="AUTOMATIC" ${this.createForm.type === "AUTOMATIC" ? "selected" : ""}>AUTOMATIC — Rotina Agendada</option>
                    <option value="SAFETY_SNAPSHOT" ${this.createForm.type === "SAFETY_SNAPSHOT" ? "selected" : ""}>SAFETY_SNAPSHOT — Snapshot de Segurança Pré-Restauração</option>
                  </select>
                </div>

                <div>
                  <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-primary); margin-bottom: 6px;">Escopo</label>
                  <select id="create-backup-scope" style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box;">
                    <option value="FULL" ${this.createForm.scope === "FULL" ? "selected" : ""}>FULL — Completo (Todas as tabelas e cadastros)</option>
                    <option value="DATABASE" ${this.createForm.scope === "DATABASE" ? "selected" : ""}>DATABASE — Apenas Banco de Dados de Engenharia</option>
                    <option value="SETTINGS" ${this.createForm.scope === "SETTINGS" ? "selected" : ""}>SETTINGS — Apenas Configurações do Sistema</option>
                  </select>
                </div>

                <div>
                  <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-primary); margin-bottom: 6px;">Descrição / Motivo (Opcional)</label>
                  <input type="text" id="create-backup-description" placeholder="Ex: Antes da atualização de tubulações..." value="${this.createForm.description}" style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box;">
                </div>
              </div>

              <div style="padding: 16px 24px; background: var(--color-surface-secondary); border-top: 1px solid var(--color-graphite-200); display: flex; justify-content: flex-end; gap: 10px;">
                <button id="btn-cancel-create-modal" style="background: transparent; border: 1px solid var(--color-graphite-300); padding: 8px 16px; border-radius: 6px; font-size: 0.875rem; cursor: pointer; color: var(--color-text-secondary);">Cancelar</button>
                <button id="btn-submit-create-backup" ${this.isCreating ? "disabled" : ""} style="background: var(--ia-action-bg, var(--color-primary)); color: var(--ia-action-fg, #F7F8F8); border: none; padding: 8px 18px; border-radius: 6px; font-size: 0.875rem; font-weight: 600; cursor: ${this.isCreating ? "not-allowed" : "pointer"}; opacity: ${this.isCreating ? "0.7" : "1"};">
                  ${this.isCreating ? "Gerando Snapshot..." : "Gerar Backup"}
                </button>
              </div>
            </div>
          </div>
        ` : ""}

        <!-- RESTORE CONFIRMATION MODAL WITH STRICT TOKEN GATING -->
        ${this.isRestoreModalOpen && this.restoreTarget ? `
          <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 20px;">
            <div style="background: var(--color-surface-primary); border-radius: 12px; width: 100%; max-width: 560px; box-shadow: 0 10px 30px rgba(0,0,0,0.3); overflow: hidden; border: 1px solid #EF4444;">

              <!-- Modal Header -->
              <div style="padding: 20px 24px; background: rgba(239, 68, 68, 0.08); border-bottom: 1px solid rgba(239, 68, 68, 0.2); display: flex; justify-content: space-between; align-items: center;">
                <div style="display: flex; align-items: center; gap: 8px;">
                  <span style="font-size: 1.5rem;">⚠️</span>
                  <h3 style="margin: 0; font-size: 1.125rem; font-weight: 700; color: #B91C1C;">
                    Confirmação Estrita de Restauração
                  </h3>
                </div>
                <button id="btn-close-restore-modal" style="background: transparent; border: none; font-size: 1.25rem; cursor: pointer; color: var(--color-text-secondary);">✕</button>
              </div>

              <!-- Modal Body -->
              <div style="padding: 24px; display: flex; flex-direction: column; gap: 16px;">
                <p style="margin: 0; font-size: 0.875rem; color: var(--color-text-primary); line-height: 1.5;">
                  Você está prestes a restaurar os dados do backup <strong style="font-family: monospace; color: var(--color-ink);">${this.restoreTarget.backupId}</strong>.
                  Esta ação substituirá ou atualizará as entidades do sistema com o conteúdo deste pacote.
                </p>

                <!-- Safety Snapshot Checkbox -->
                <div style="background: rgba(16, 185, 129, 0.08); border: 1px solid #10B981; border-radius: 8px; padding: 12px 14px;">
                  <label style="display: flex; align-items: center; gap: 8px; cursor: pointer; font-size: 0.875rem; font-weight: 600; color: #047857;">
                    <input type="checkbox" id="chk-safety-snapshot" ${this.createSafetySnapshot ? "checked" : ""} style="width: 16px; height: 16px;">
                    <span>Gerar Snapshot de Segurança Automático antes da restauração (Recomendado)</span>
                  </label>
                  <p style="margin: 4px 0 0 24px; font-size: 0.75rem; color: var(--color-text-secondary);">
                    Garante um ponto de restauração imediato prévio para rollback caso necessário.
                  </p>
                </div>

                <!-- Confirmation Token Box -->
                <div style="background: var(--color-surface-secondary); border: 1px solid var(--color-graphite-300); border-radius: 8px; padding: 14px;">
                  <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-primary); margin-bottom: 6px;">
                    Digite o token de confirmação exatamente como exibido abaixo:
                  </label>
                  <div style="background: var(--color-bg-primary); border: 1px solid var(--color-graphite-300); border-radius: 6px; padding: 8px 12px; font-family: monospace; font-size: 0.9375rem; font-weight: 700; color: var(--color-ink); letter-spacing: 0.5px; user-select: all; text-align: center; margin-bottom: 10px;">
                    ${expectedToken}
                  </div>
                  <input type="text" id="input-restore-token" placeholder="Digite o token aqui..." value="${this.confirmationInput}" style="width: 100%; padding: 10px 12px; border: 2px solid ${isTokenMatch ? "#10B981" : "var(--color-graphite-300)"}; border-radius: 6px; font-size: 0.875rem; font-family: monospace; box-sizing: border-box; text-transform: uppercase;">
                </div>
              </div>

              <!-- Modal Footer -->
              <div style="padding: 16px 24px; background: var(--color-surface-secondary); border-top: 1px solid var(--color-graphite-200); display: flex; justify-content: flex-end; gap: 10px;">
                <button id="btn-cancel-restore-modal" style="background: transparent; border: 1px solid var(--color-graphite-300); padding: 8px 16px; border-radius: 6px; font-size: 0.875rem; cursor: pointer; color: var(--color-text-secondary);">Cancelar</button>
                <button id="btn-confirm-execute-restore" ${!isTokenMatch || this.isRestoring ? "disabled" : ""} style="background: ${isTokenMatch ? "#DC2626" : "var(--color-graphite-400)"}; color: #FFFFFF; border: none; padding: 8px 20px; border-radius: 6px; font-size: 0.875rem; font-weight: 700; cursor: ${!isTokenMatch || this.isRestoring ? "not-allowed" : "pointer"}; opacity: ${this.isRestoring ? "0.7" : "1"}; display: flex; align-items: center; gap: 6px;">
                  <span>${this.isRestoring ? "⏳" : "🔄"}</span>
                  <span>${this.isRestoring ? "Restaurando..." : "Confirmar e Executar Restauração"}</span>
                </button>
              </div>
            </div>
          </div>
        ` : ""}

      </div>
    `;

    this.bindEvents();
  }

  private bindEvents(): void {
    // Breadcrumb navigation
    this.querySelector("#btn-back-admin")?.addEventListener("click", () => {
      router.navigate("/administration");
    });

    // Close alert
    this.querySelector("#btn-close-alert")?.addEventListener("click", () => {
      this.alertMessage = null;
      this.render();
    });

    // Retention cleanup
    this.querySelector("#btn-retention-cleanup")?.addEventListener("click", () => {
      this.handleRetentionCleanup();
    });

    // Open Create Modal
    this.querySelector("#btn-open-create-backup")?.addEventListener("click", () => {
      this.isCreateModalOpen = true;
      this.render();
    });

    // Tabs
    this.querySelectorAll(".nav-tab").forEach(tab => {
      tab.addEventListener("click", () => {
        const t = tab.getAttribute("data-tab") as any;
        if (t) {
          this.activeTab = t;
          this.render();
        }
      });
    });

    // Download Backup buttons
    this.querySelectorAll(".btn-download-backup").forEach(btn => {
      btn.addEventListener("click", () => {
        const id = btn.getAttribute("data-id");
        if (id) this.handleDownloadBackup(id);
      });
    });

    // Delete Backup buttons
    this.querySelectorAll(".btn-delete-backup").forEach(btn => {
      btn.addEventListener("click", () => {
        const id = btn.getAttribute("data-id");
        if (id) this.handleDeleteBackup(id);
      });
    });

    // Trigger Restore from list
    this.querySelectorAll(".btn-trigger-restore").forEach(btn => {
      btn.addEventListener("click", () => {
        const id = btn.getAttribute("data-id");
        const type = btn.getAttribute("data-type") || "MANUAL";
        if (id) this.openRestoreModal(id, type, false);
      });
    });

    // Create Modal handlers
    this.querySelector("#btn-close-create-modal")?.addEventListener("click", () => {
      this.isCreateModalOpen = false;
      this.render();
    });
    this.querySelector("#btn-cancel-create-modal")?.addEventListener("click", () => {
      this.isCreateModalOpen = false;
      this.render();
    });
    this.querySelector("#create-backup-type")?.addEventListener("change", (e: any) => {
      this.createForm.type = e.target.value;
    });
    this.querySelector("#create-backup-scope")?.addEventListener("change", (e: any) => {
      this.createForm.scope = e.target.value;
    });
    this.querySelector("#create-backup-description")?.addEventListener("input", (e: any) => {
      this.createForm.description = e.target.value;
    });
    this.querySelector("#btn-submit-create-backup")?.addEventListener("click", () => {
      this.handleCreateBackupSubmit();
    });

    // File Dropzone / Select
    const dropzone = this.querySelector("#dropzone");
    const fileInput = this.querySelector("#input-backup-file") as HTMLInputElement;

    dropzone?.addEventListener("click", () => {
      fileInput?.click();
    });

    fileInput?.addEventListener("change", () => {
      if (fileInput.files && fileInput.files[0]) {
        this.handleFileUpload(fileInput.files[0]);
      }
    });

    dropzone?.addEventListener("dragover", (e) => {
      e.preventDefault();
      (dropzone as HTMLElement).style.borderColor = "var(--ia-primary)";
      (dropzone as HTMLElement).style.background = "var(--color-surface-secondary)";
    });

    dropzone?.addEventListener("dragleave", () => {
      (dropzone as HTMLElement).style.borderColor = "var(--color-graphite-300)";
      (dropzone as HTMLElement).style.background = "var(--color-surface-primary)";
    });

    dropzone?.addEventListener("drop", (e: any) => {
      e.preventDefault();
      (dropzone as HTMLElement).style.borderColor = "var(--color-graphite-300)";
      (dropzone as HTMLElement).style.background = "var(--color-surface-primary)";
      if (e.dataTransfer?.files && e.dataTransfer.files[0]) {
        this.handleFileUpload(e.dataTransfer.files[0]);
      }
    });

    // Proceed restore from upload
    this.querySelector("#btn-proceed-restore-upload")?.addEventListener("click", () => {
      if (this.validationResult?.manifest && this.uploadedPackage) {
        this.openRestoreModal(
          this.validationResult.manifest.id,
          this.validationResult.manifest.type,
          true,
          this.uploadedPackage
        );
      }
    });

    // Restore Modal handlers
    this.querySelector("#btn-close-restore-modal")?.addEventListener("click", () => {
      this.isRestoreModalOpen = false;
      this.render();
    });
    this.querySelector("#btn-cancel-restore-modal")?.addEventListener("click", () => {
      this.isRestoreModalOpen = false;
      this.render();
    });
    this.querySelector("#chk-safety-snapshot")?.addEventListener("change", (e: any) => {
      this.createSafetySnapshot = e.target.checked;
    });
    this.querySelector("#input-restore-token")?.addEventListener("input", (e: any) => {
      this.confirmationInput = e.target.value;
      const expectedToken = this.restoreTarget ? generateRestoreConfirmationToken(this.restoreTarget.backupId) : "";
      const isMatch = this.confirmationInput.trim().toUpperCase() === expectedToken;
      const btn = this.querySelector("#btn-confirm-execute-restore") as HTMLButtonElement;
      if (btn) {
        btn.disabled = !isMatch || this.isRestoring;
        btn.style.background = isMatch ? "#DC2626" : "var(--color-graphite-400)";
        btn.style.cursor = isMatch ? "pointer" : "not-allowed";
      }
      if (e.target) {
        e.target.style.borderColor = isMatch ? "#10B981" : "var(--color-graphite-300)";
      }
    });
    this.querySelector("#btn-confirm-execute-restore")?.addEventListener("click", () => {
      this.handleExecuteRestore();
    });
  }
}

if (!customElements.get("app-backup-restore-page")) {
  customElements.define("app-backup-restore-page", BackupRestorePage);
}
